import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsTable,
    LanguageBaseContext,
} from "dmsbasereact";
import { Typography, Stack, IconButton, Tooltip, Chip, Box } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatCnpj } from "../../utils/formatters";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { InstitutionTypeEnum } from "../../constants/InstitutionType";
import { ReportFilters } from "../ReportsPage/components/ReportFilters";
import { useReport } from "../ReportsPage/components/useReport";

export const PartnerBranchPage = () => {
    const navigate = useNavigate();
    const { language } = useContext(LanguageBaseContext);

    const handleNavigateToDetail = (row) => {
        navigate(`${APP_ROUTES.PartnerBranch}/${row.organization_branch_pk}`, {
            state: {
                matrizName: row.name,
                matrizCnpj: row.organization_branch?.cnpj,
                matrizIsPublic: !!row.is_public,
            },
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    const filterFields = useMemo(() => [
        {
            name: "name",
            label: intl.get("InstitutionName").d("Institution Name"),
            type: "text",
        },
        {
            name: "cnpj",
            label: intl.get("CNPJ").d("CNPJ"),
            type: "text",
        },
        {
            name: "category",
            label: intl.get("Category").d("Category"),
            type: "select",
            options: Object.values(InstitutionTypeEnum).map(t => ({
                value: String(t.category),
                label: t.label,
            })),
        },
        {
            name: "branch_names",
            label: intl.get("AssociatedBranches").d("Associated Branches"),
            type: "text",
        },
    ], [language]);

    const {
        state: { tableRef, filters, clearKey, filterFields: processedFilterFields, columnVisibility },
        handlers: {
            setFilters,
            handleApplyFilters,
            handleClearFilters,
            handleRefresh,
            buildPreFilter,
            buildExportUrl,
            setColumnVisibility,
        },
    } = useReport({
        exportUrl: "/partner/branch/export",
        filterFields,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    const header = useMemo(() => [
        {
            label: intl.get("InstitutionName").d("Institution Name"),
            column_name: "name",
            sortable: true,
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "cnpj",
            function: (value) => formatCnpj(value),
        },
        {
            label: intl.get("Category").d("Category"),
            column_name: "category",
            sortable: false,
            width: "180px",
            element: (row) => {
                const labels = row.organization_branch?.category_labels;
                if (!labels || labels.length === 0) return "-";
                return (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {labels.map((label, idx) => (
                            <Chip key={idx} label={label} size="small" variant="outlined" />
                        ))}
                    </Box>
                );
            },
        },
        {
            label: intl.get("IsPublicInstitutionShort").d("Public"),
            column_name: "is_public",
            sortable: false,
            function: (value) => value ? intl.get("Yes").d("Yes") : intl.get("No").d("No"),
        },
        {
            label: intl.get("AssociatedBranches").d("Associated Branches"),
            column_name: "branch_names",
            sortable: false,
            width: "250px",
            element: (row) => {
                const names = row.branch_names;
                if (!names || names.length === 0) return "-";
                return (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {names.map((name, idx) => (
                            <Chip key={idx} label={name} size="small" variant="outlined" />
                        ))}
                    </Box>
                );
            },
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            sortable: false,
            element: (row) => (
                <Tooltip title={intl.get("ViewBranches").d("View Branches")}>
                    <IconButton
                        size="small"
                        onClick={() => handleNavigateToDetail(row)}
                        sx={{
                            backgroundColor: "success.main",
                            color: "#fff",
                            "&:hover": { backgroundColor: "success.dark" },
                        }}
                    >
                        <ArrowForwardIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ], [language]);

    return (
        <DmsPage name={intl.get("Branches").d("Branches")}>
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                            {intl.get("Branches").d("Branches")}
                        </Typography>
                        <ReportFilters
                            filterFields={processedFilterFields}
                            filters={filters}
                            onFiltersChange={setFilters}
                            onApply={handleApplyFilters}
                            onClear={handleClearFilters}
                            onRefresh={handleRefresh}
                            exportUrl={buildExportUrl()}
                            columns={header}
                            columnVisibility={columnVisibility}
                            onColumnVisibilityChange={setColumnVisibility}
                            showPdf={true}
                            showDateRange={false}
                        />
                    </Stack>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        key={`partner-branch-table-${clearKey}`}
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        ref={tableRef}
                        url="/partner/branch/table"
                        pre_filter={buildPreFilter()}
                        toolbar={<></>}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};

export default PartnerBranchPage;
